/**
 * AiTutorContext / AiTutorProvider
 *
 * The ai-tutor data plane for the recording view — the analog of socket-server
 * for the class path. Must be rendered INSIDE <LiveKitRoom> so it can read the
 * room's metadata.
 *
 * Everything comes from room metadata (no Sanity lookups — the recorder has no
 * Sanity access; the `production` dataset is private):
 *  - `lectures` — map of elementId -> presentation player URL. Seeded by
 *    lms-backend at room creation and preserved by the agent on every metadata
 *    checkpoint, so the recorder always has the link to render the deck.
 *  - `element_id` — the CURRENT deck (the one `position` applies to).
 *  - position fields — AUTHORITATIVE slide position; the agent refreshes
 *    metadata at every lesson checkpoint, so it's stateful and reconnect/
 *    late-join safe.
 *
 * Stopping the recording is owned by the ai-tutor agent (it POSTs lms-backend on
 * lesson end / student-leave), so this page does nothing on lesson end.
 */
import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { useRoomInfo } from '@livekit/components-react';

import type { SlidePosition } from 'components/AiTutorPresentation';

interface AiTutorState {
  /** elementId -> presentation player URL, from room metadata `lectures`. */
  lectures: Record<string, string>;
  /** Deck ids to render (keys of `lectures`), current deck first. */
  elements: string[];
  /** The CURRENT deck — the one `position` applies to. */
  elementId: string | null;
  position: SlidePosition;
}

const DEFAULT_POSITION: SlidePosition = { slide: 0, step: 0, totalClicks: 0 };

const AiTutorContext = createContext<AiTutorState | undefined>(undefined);

export const useAiTutor = (): AiTutorState => {
  const ctx = useContext(AiTutorContext);
  if (!ctx) {
    throw new Error('useAiTutor must be used within an AiTutorProvider');
  }
  return ctx;
};

const parseMeta = (raw: string | undefined): Record<string, unknown> | null => {
  if (!raw) return null;
  try {
    const m = JSON.parse(raw);
    return m && typeof m === 'object' ? m : null;
  } catch {
    return null;
  }
};

/**
 * `slide = slide_number` is solid; the within-slide `step` mapping is a best
 * guess (`segment_index`) pending a live-session check — plan item F1.
 */
const toPosition = (m: Record<string, unknown> | null): SlidePosition | null => {
  if (!m) return null;
  return {
    slide: Number(m.slide_number ?? 0),
    step: Number(m.segment_index ?? 0),
    totalClicks: Number(m.total_clicks ?? 0),
  };
};

/** Extract the `lectures` map from metadata, keeping only string url values. */
const parseLectures = (m: Record<string, unknown> | null): Record<string, string> => {
  const raw = m?.lectures;
  // `typeof [] === 'object'` — exclude arrays so a malformed `lectures` array
  // doesn't yield numeric-string keys ('0', '1', …).
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Record<string, string> = {};
  for (const [id, url] of Object.entries(raw as Record<string, unknown>)) {
    if (id && typeof url === 'string' && url) out[id] = url;
  }
  return out;
};

interface AiTutorProviderProps {
  children: ReactNode;
  /** First deck to mark active before the agent publishes metadata. */
  bootstrapElementId?: string;
}

export const AiTutorProvider = ({ children, bootstrapElementId }: AiTutorProviderProps) => {
  const { metadata } = useRoomInfo(); // re-renders on RoomMetadataChanged
  const [lectures, setLectures] = useState<Record<string, string>>({});
  const [elementId, setElementId] = useState<string | null>(bootstrapElementId ?? null);
  const [position, setPosition] = useState<SlidePosition>(DEFAULT_POSITION);

  // Authoritative lecture links / current element / position, from room metadata.
  // `position` is the state FOR `element_id`.
  useEffect(() => {
    const m = parseMeta(metadata);
    if (!m) return;

    const eid = typeof m.element_id === 'string' && m.element_id ? m.element_id : null;
    if (eid) setElementId(eid);

    // Merge so an already-known link survives a metadata write that omits it.
    const next = parseLectures(m);
    if (Object.keys(next).length) {
      setLectures((prev) => ({ ...prev, ...next }));
    }

    const pos = toPosition(m);
    if (pos) setPosition(pos);
  }, [metadata]);

  // Decks to mount: every resolved lecture, with the current deck first so it
  // renders even if metadata listed the link before the element_id (or vice
  // versa). The active deck is `elementId`.
  const elements = useMemo(() => {
    const keys = Object.keys(lectures);
    if (elementId && !keys.includes(elementId)) keys.unshift(elementId);
    return keys;
  }, [lectures, elementId]);

  const value = useMemo(
    () => ({ lectures, elements, elementId, position }),
    [lectures, elements, elementId, position],
  );

  return <AiTutorContext.Provider value={value}>{children}</AiTutorContext.Provider>;
};
